import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import TextField from '../../components/formfields/TextField';
import { useTheme } from '../../context/ThemeContext';
import useForgotPassword from '../../hooks/useForgotPassword';
import { ChevronDown, ChevronUp } from 'lucide-react';

const Spinner = ({ size = 16, color = '#ffffff' }) => (
  <div style={{
    width: `${size}px`,
    height: `${size}px`,
    border: `2px solid ${color}33`,
    borderTop: `2px solid ${color}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }} />
);

Spinner.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string
};

// Helper to extract value from event or object
const extractValue = (data) => {
  if (data && typeof data === 'object' && data.target && data.target.value !== undefined) {
    return data.target.value;
  }
  if (data && typeof data === 'object' && data.value !== undefined) {
    return data.value;
  }
  return data;
};

const ForgotPassword = ({ 
  brandId,
  onPasswordReset, 
  onCancel,
  triggerText = 'Forgot your password?',
  startExpanded = false
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [isExpanded, setIsExpanded] = useState(startExpanded);
  const [checkingSms, setCheckingSms] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [lastFour, setLastFour] = useState(null);
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'password'
  const [deliveryMethod, setDeliveryMethod] = useState('email'); // 'email' | 'sms'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResetCode, setPasswordResetCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    checkPhoneStatus, 
    sendResetCode,
    verifyResetCode,
    resetPassword
  } = useForgotPassword(brandId);

  const handleToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      setStep('email');
      setEmail('');
      setCode('');
      setPassword('');
      setConfirmPassword('');
      setDeliveryMethod('email');
      setError(null);
    }
  }, [isExpanded]);


  useEffect(() => {
    const checkSmsVerification = async () => {
      if (deliveryMethod === 'sms' && email) {
        setCheckingSms(true);
        try {
          const status = await checkPhoneStatus(email);
          if (!status.mobile_verified) {
            setError('The phone number associated with this email is not verified.');
            setDeliveryMethod('email');
          } else {
            setMobileVerified(true);
            setLastFour(status.last_four);
            setError(null);
          }
        } catch (err) {
          setError(err.message || 'Failed to check phone verification status.');
        } finally {
          setCheckingSms(false);
        }
      }
    };
    checkSmsVerification();
  },[email, deliveryMethod]);

  const handleDeliveryMethodChange = useCallback((method) => {
    setDeliveryMethod(method);
  }, []);

  const handleEmailChange = useCallback((value) => {
    setEmail(extractValue(value));
  }, []);

  const handleCodeChange = useCallback((value) => {
    setCode(extractValue(value));
  }, []);

  const handlePasswordChange = useCallback((value) => {
    setPassword(extractValue(value));
  }, []);

  const handleConfirmPasswordChange = useCallback((value) => {
    setConfirmPassword(extractValue(value));
  }, []);

  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await sendResetCode(email, deliveryMethod);
      setStep('code');
    } catch (err) {
      setError(err.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, deliveryMethod, sendResetCode]);

  const handleCodeSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const results = await verifyResetCode(email, code, deliveryMethod);
      if (results.password_reset_token) {
        setPasswordResetCode(results.password_reset_token);
      }
      setStep('password');
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, code, deliveryMethod, verifyResetCode]);

  const handlePasswordSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const result = await resetPassword(passwordResetCode, password);
      onPasswordReset?.(password, result);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [password, confirmPassword, passwordResetCode, resetPassword, onPasswordReset]);

  const handleBack = useCallback(() => {
    setError(null);
    if (step === 'code') {
      setStep('email');
    }
    if (step === 'password') {
      setStep('code');
    }
  }, [step]);

  const handleCancelClick = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      handleToggle();
    }
  }, [onCancel, handleToggle]);

  const triggerClassName = clsx(
    'flex items-center gap-2 text-sm font-medium cursor-pointer transition-colors',
    {
      'text-blue-400 hover:text-blue-300': isDark,
      'text-blue-600 hover:text-blue-700': !isDark
    }
  );

  const containerClassName = clsx(
    'rounded-lg',
    {
      'bg-slate-800': isDark,
      'bg-white': !isDark
    }
  );

  const headerClassName = clsx(
    'text-2xl font-semibold mb-2',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const subtitleClassName = clsx(
    'mb-6',
    {
      'text-slate-400': isDark,
      'text-gray-600': !isDark
    }
  );

  const buttonClassName = clsx(
    'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:cursor-not-allowed',
    {
      'bg-slate-900 hover:bg-slate-700 text-white focus:ring-slate-500': !isDark,
      'bg-slate-700 hover:bg-slate-600 text-white focus:ring-slate-500': isDark
    }
  );

  const secondaryButtonClassName = clsx(
    'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-gray-200 hover:bg-gray-300 text-gray-900 focus:ring-gray-400': !isDark,
      'bg-slate-600 hover:bg-slate-500 text-white focus:ring-slate-400': isDark
    }
  );

  const radioLabelClassName = clsx(
    'flex items-center gap-2 cursor-pointer',
    {
      'text-slate-200': isDark,
      'text-gray-700': !isDark
    }
  );

  const infoBoxClassName = clsx(
    'p-3 rounded-lg border text-sm',
    {
      'bg-blue-900/20 border-blue-600 text-blue-300': isDark,
      'bg-blue-50 border-blue-200 text-blue-700': !isDark
    }
  );

  // Collapsed State
  if (!isExpanded) {
    return (
      <div className="py-2">
        <button
          type="button"
          onClick={handleToggle}
          className={triggerClassName}
        >
          <span>{triggerText}</span>
          <ChevronDown size={16} />
        </button>
      </div>
    );
  }

  // Expanded State
  return (
    <div className={containerClassName}>
      <div className="p-6">
        {/* Collapse Button */}
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={handleToggle}
            className={clsx(
              'flex items-center gap-1 text-sm transition-colors',
              {
                'text-slate-400 hover:text-slate-300': isDark,
                'text-gray-600 hover:text-gray-700': !isDark
              }
            )}
          >
            <span>Close</span>
            <ChevronUp size={16} />
          </button>
        </div>

        {step === 'email' && (
          <>
            <h2 className={headerClassName}>Reset your password</h2>
            <p className={subtitleClassName}>
              Enter your email address to receive your verification code via SMS.
            </p>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <TextField
                id="reset-email"
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={(e) => setEmail(e.target.value.trim())}
                placeholder="Enter your email"
                required
              />

              {/* Delivery Method Selection */}
              <div>
                <label className={clsx(
                  'block text-sm font-medium mb-3',
                  {
                    'text-slate-200': isDark,
                    'text-gray-700': !isDark
                  }
                )}>
                  The code will be sent via SMS to your registered mobile number
                </label>
                {/* <div className="flex gap-6">
                  <label className={radioLabelClassName}>
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="email"
                      checked={deliveryMethod === 'email'}
                      onChange={(e) => handleDeliveryMethodChange(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span>Email</span>
                  </label>
                  <label className={radioLabelClassName}>
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value="sms"
                      checked={deliveryMethod === 'sms'}
                      onChange={(e) => handleDeliveryMethodChange(e.target.value)}
                      className="w-4 h-4"
                    />
                    <span>SMS</span>
                  </label>
                </div> */}
              </div>

              {deliveryMethod === 'sms' && !checkingSms && mobileVerified && lastFour?.length && (
                <div className={infoBoxClassName}>
                  {`We'll send the verification code to the verified mobile number ending in ${lastFour} associated with your account.`}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className={secondaryButtonClassName}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !email?.length}
                  className={buttonClassName}
                >
                  <div className="flex items-center justify-center gap-2">
                    {loading && <Spinner />}
                    Send Code
                  </div>
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <h2 className={headerClassName}>Enter verification code</h2>
            <p className={subtitleClassName}>
              We sent a verification code to your {deliveryMethod === 'email' ? 'email address' : 'mobile phone'}
            </p>

            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <TextField
                id="reset-code"
                label="Verification Code"
                value={code}
                onChange={handleCodeChange}
                onBlur={(e) => setCode(e.target.value.trim())}
                placeholder="Enter 6-digit code"
                required
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  className={secondaryButtonClassName}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={buttonClassName}
                >
                  <div className="flex items-center justify-center gap-2">
                    {loading && <Spinner />}
                    Verify Code
                  </div>
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'password' && (
          <>
            <h2 className={headerClassName}>Create new password</h2>
            <p className={subtitleClassName}>
              Enter your new password below.
            </p>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <TextField
                id="new-password"
                label="New Password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                onBlur={(e) => setPassword(e.target.value.trim())}
                placeholder="Enter new password"
                required
              />

              <TextField
                id="confirm-password"
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={(e) => setConfirmPassword(e.target.value.trim())}
                placeholder="Confirm new password"
                required
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  className={secondaryButtonClassName}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={buttonClassName}
                >
                  <div className="flex items-center justify-center gap-2">
                    {loading && <Spinner />}
                    Reset Password
                  </div>
                </button>
              </div>
            </form>
          </>
        )}

        {checkingSms && <Spinner size={20} color={isDark ? '#ffffff' : '#000000'} />}

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

ForgotPassword.propTypes = {
  brandId: PropTypes.string,
  onPasswordReset: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  triggerText: PropTypes.string,
  startExpanded: PropTypes.bool
};

export default ForgotPassword;